"use client";

import { useEffect, useRef } from "react";
import { usePathname } from "next/navigation";
import { useCurrentUser } from "@zapier/client";
import { useRouterParams } from "../../../hooks/useRouterParams";
import { useDefaultViewStorage } from "./useDefaultViewStorage";
import { DefaultViewSelection } from "./types";

interface Options {
  hasActiveFilters: boolean;
}

function isDocumentReloadOrRestore(): boolean {
  if (globalThis.window === undefined) return false;
  const [entry] = globalThis.performance.getEntriesByType("navigation");
  const type = (entry as PerformanceNavigationTiming | undefined)?.type;
  return type === "reload" || type === "back_forward";
}

/**
 * Default to "Owned by me" on first landing when no saved view exists and
 * the caller reports no active filter/search intent. Only fires on fresh
 * navigation or in-app SPA routing — page reloads and bfcache restores
 * preserve whatever URL state the user already had.
 */
export function useDefaultOwnedByMe({ hasActiveFilters }: Options): void {
  const { user } = useCurrentUser();
  const { hasStoredView, isReady } = useDefaultViewStorage();
  const { setParams } = useRouterParams();
  const pathname = usePathname();
  // `setParams` re-creates each render — keep the effect one-shot per mount.
  const hasApplied = useRef(false);
  // Use window.location since useSearchParams can lag during hydration.
  const hadUrlParametersOnMount = useRef(
    globalThis.window !== undefined && globalThis.window.location.search !== "",
  );

  useEffect(() => {
    if (hasApplied.current) return;
    if (isDocumentReloadOrRestore()) {
      hasApplied.current = true;
      return;
    }
    if (hadUrlParametersOnMount.current) {
      hasApplied.current = true;
      return;
    }
    if (!isReady) return;
    if (!user) return;

    if (hasStoredView(pathname as keyof DefaultViewSelection)) {
      hasApplied.current = true;
      return;
    }
    if (hasActiveFilters) {
      hasApplied.current = true;
      return;
    }

    hasApplied.current = true;
    setParams({ owners: String(user.id), page: 1 });
  }, [user, isReady, hasStoredView, pathname, hasActiveFilters, setParams]);
}
